import React, { useEffect, useMemo, useState } from "react";
import {
  Timestamp,
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import {
  buildDespatchedItems,
  buildLocationMap,
  buildProductMap,
  buildWarehouseBookedOutItems,
  dateRangeFromInputs,
  exportDespatchWarehouseWorkbook,
  formatDateTime,
  formatPeriodLabel,
  localDateInputValue,
} from "./despatchWarehouseReportHelper";

const INPUT_CLASS =
  "rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary";

function snapshotRows(snapshot) {
  return snapshot.docs.map((documentSnapshot) => ({
    id: documentSnapshot.id,
    ...documentSnapshot.data(),
  }));
}

function displayValue(value) {
  return value === "" || value == null ? "—" : String(value);
}

function HeadlineCard({ title, count, period, open, onToggle }) {
  return (
    <article className="rounded-xl border border-primary/30 bg-white p-6 shadow-sm">
      <h2 className="text-lg font-semibold text-primary">{title}</h2>
      <div className="mt-3 text-6xl font-bold text-primary">{count}</div>
      <p className="mt-3 text-sm text-gray-600">{period}</p>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="mt-5 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-hover focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
      >
        {open ? "Hide items" : "View items"}
      </button>
    </article>
  );
}

function ItemsTable({ title, rows, columns }) {
  return (
    <section className="rounded-lg border border-primary/20 bg-white shadow-sm">
      <div className="border-b border-primary/20 px-4 py-3">
        <h2 className="font-semibold text-primary">
          {title} ({rows.length})
        </h2>
      </div>
      {rows.length === 0 ? (
        <div className="border-l-4 border-primary bg-primary/5 p-6 text-center text-gray-600">
          No items were recorded in this period.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse text-left text-sm">
            <thead className="bg-primary text-xs uppercase tracking-wide text-white">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className="whitespace-nowrap border-b border-primary px-3 py-3"
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="hover:bg-primary/5">
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className="whitespace-nowrap border-b border-gray-100 px-3 py-3 text-gray-700"
                    >
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const DESPATCH_COLUMNS = [
  {
    key: "date",
    label: "Date and time",
    render: (row) => displayValue(formatDateTime(row.scannedAt)),
  },
  {
    key: "report",
    label: "Despatch report",
    render: (row) => displayValue(row.reportNumber),
  },
  {
    key: "barcode",
    label: "Barcode",
    render: (row) => (
      <span className="font-mono">{displayValue(row.barcode)}</span>
    ),
  },
  {
    key: "serial",
    label: "Serial number",
    render: (row) => (
      <span className="font-mono">{displayValue(row.serialNumber)}</span>
    ),
  },
  {
    key: "productCode",
    label: "Product code",
    render: (row) => displayValue(row.productCode),
  },
  {
    key: "productId",
    label: "Product ID",
    render: (row) => (
      <span className="font-mono">{displayValue(row.productId)}</span>
    ),
  },
  {
    key: "special",
    label: "Type / special",
    render: (row) => displayValue(row.special),
  },
];

const WAREHOUSE_COLUMNS = [
  {
    key: "date",
    label: "Booked-out date and time",
    render: (row) => displayValue(formatDateTime(row.bookedOutAt)),
  },
  {
    key: "barcode",
    label: "Barcode",
    render: (row) => (
      <span className="font-mono">{displayValue(row.barcode)}</span>
    ),
  },
  {
    key: "serial",
    label: "Serial number",
    render: (row) => (
      <span className="font-mono">{displayValue(row.serialNumber)}</span>
    ),
  },
  {
    key: "productCode",
    label: "Product code",
    render: (row) => displayValue(row.productCode),
  },
  {
    key: "productId",
    label: "Product ID",
    render: (row) => (
      <span className="font-mono">{displayValue(row.productId)}</span>
    ),
  },
  {
    key: "special",
    label: "Type / special",
    render: (row) => displayValue(row.special),
  },
  {
    key: "location",
    label: "Last warehouse location",
    render: (row) => displayValue(row.location),
  },
  {
    key: "reason",
    label: "Removal reason",
    render: (row) => displayValue(row.reason),
  },
  {
    key: "notes",
    label: "Notes",
    render: (row) => displayValue(row.notes),
  },
];

export default function DespatchWarehouseReport() {
  const today = useMemo(() => localDateInputValue(), []);
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [appliedDates, setAppliedDates] = useState({
    dateFrom: today,
    dateTo: today,
  });
  const [despatchedItems, setDespatchedItems] = useState([]);
  const [warehouseItems, setWarehouseItems] = useState([]);
  const [excludedBackupCount, setExcludedBackupCount] = useState(0);
  const [showDespatched, setShowDespatched] = useState(false);
  const [showWarehouse, setShowWarehouse] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function loadReport() {
      const range = dateRangeFromInputs(
        appliedDates.dateFrom,
        appliedDates.dateTo
      );
      if (!range) {
        setError("Please select a valid date range.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      try {
        const completedReportsQuery = query(
          collection(db, "despatchreports"),
          where("completed", "==", true)
        );
        const archiveDateQuery = query(
          collection(db, "warehouse_archive"),
          where("BookedOutTime", ">=", Timestamp.fromDate(range.start)),
          where("BookedOutTime", "<=", Timestamp.fromDate(range.end))
        );

        const [
          reportSnapshot,
          detailSnapshot,
          archiveSnapshot,
          productSnapshot,
          locationSnapshot,
        ] = await Promise.all([
          getDocs(completedReportsQuery),
          getDocs(collection(db, "despatchreportsdetailed")),
          getDocs(archiveDateQuery),
          getDocs(collection(db, "cyl_info")),
          getDocs(collection(db, "warehouse_locations")),
        ]);
        if (cancelled) return;

        const productMap = buildProductMap(snapshotRows(productSnapshot));
        const locationMap = buildLocationMap(snapshotRows(locationSnapshot));
        const nextDespatchedItems = buildDespatchedItems({
          reports: snapshotRows(reportSnapshot),
          details: snapshotRows(detailSnapshot),
          productMap,
          start: range.start,
          end: range.end,
        });
        const warehouseResult = buildWarehouseBookedOutItems({
          archiveRecords: snapshotRows(archiveSnapshot),
          productMap,
          locationMap,
          start: range.start,
          end: range.end,
        });

        setDespatchedItems(nextDespatchedItems);
        setWarehouseItems(warehouseResult.items);
        setExcludedBackupCount(warehouseResult.excludedBackupCount);
      } catch (loadError) {
        if (!cancelled) {
          console.error("Despatch and warehouse report failed:", loadError);
          setError(loadError?.message || String(loadError));
          setDespatchedItems([]);
          setWarehouseItems([]);
          setExcludedBackupCount(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadReport();
    return () => {
      cancelled = true;
    };
  }, [appliedDates]);

  const periodLabel = formatPeriodLabel(
    appliedDates.dateFrom,
    appliedDates.dateTo
  );

  function applyDates(event) {
    event.preventDefault();
    if (!dateRangeFromInputs(dateFrom, dateTo)) {
      setError("Date from must be on or before date to.");
      return;
    }
    setShowDespatched(false);
    setShowWarehouse(false);
    setAppliedDates({ dateFrom, dateTo });
  }

  function exportReport() {
    exportDespatchWarehouseWorkbook({
      dateFrom: appliedDates.dateFrom,
      dateTo: appliedDates.dateTo,
      despatchedItems,
      warehouseItems,
    });
  }

  return (
    <main className="min-h-screen bg-gray-100 p-4 md:p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 border-l-4 border-primary pl-4">
          <div>
            <h1 className="text-3xl font-bold text-primary">
              Despatch and Warehouse Report
            </h1>
            <p className="mt-1 text-gray-600">
              Completed despatch entries and warehouse book-outs for the
              selected dates.
            </p>
          </div>
        </div>

        <form
          onSubmit={applyDates}
          className="mb-5 rounded-lg border border-primary/20 bg-white p-4 shadow-sm"
        >
          <div className="flex flex-wrap items-end gap-4">
            <label className="block">
              <span className="mb-1 block text-sm font-semibold text-primary">
                Date from
              </span>
              <input
                aria-label="Date from"
                type="date"
                value={dateFrom}
                onChange={(event) => setDateFrom(event.target.value)}
                className={INPUT_CLASS}
                required
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-semibold text-primary">
                Date to
              </span>
              <input
                aria-label="Date to"
                type="date"
                value={dateTo}
                onChange={(event) => setDateTo(event.target.value)}
                className={INPUT_CLASS}
                required
              />
            </label>
            <button
              type="submit"
              disabled={loading}
              className="rounded-md bg-primary px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-hover focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? "Loading…" : "Refresh report"}
            </button>
            <button
              type="button"
              onClick={exportReport}
              disabled={loading || Boolean(error)}
              className="rounded-md border border-primary bg-white px-5 py-2 text-sm font-semibold text-primary shadow-sm hover:bg-primary/10 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Export report
            </button>
          </div>
        </form>

        <div className="mb-5 text-center text-xl font-semibold text-primary">
          {periodLabel}
        </div>

        {error && (
          <div
            role="alert"
            className="mb-5 rounded-lg border border-rose-300 bg-rose-50 p-4 text-rose-800"
          >
            {error}
          </div>
        )}

        {excludedBackupCount > 0 && !loading && !error && (
          <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            {excludedBackupCount} clearly identified warehouse backup record
            {excludedBackupCount === 1 ? " was" : "s were"} left out.
          </div>
        )}

        {loading ? (
          <div
            role="status"
            className="rounded-lg border border-primary/30 bg-white p-12 text-center font-medium text-primary shadow-sm"
          >
            Loading report…
          </div>
        ) : error ? null : (
          <>
            <div className="grid gap-5 md:grid-cols-2">
              <HeadlineCard
                title="Despatched"
                count={despatchedItems.length}
                period={periodLabel}
                open={showDespatched}
                onToggle={() => setShowDespatched((current) => !current)}
              />
              <HeadlineCard
                title="Booked out of warehouse"
                count={warehouseItems.length}
                period={periodLabel}
                open={showWarehouse}
                onToggle={() => setShowWarehouse((current) => !current)}
              />
            </div>

            <div className="mt-5 space-y-5">
              {showDespatched && (
                <ItemsTable
                  title="Despatched items"
                  rows={despatchedItems}
                  columns={DESPATCH_COLUMNS}
                />
              )}
              {showWarehouse && (
                <ItemsTable
                  title="Warehouse booked-out items"
                  rows={warehouseItems}
                  columns={WAREHOUSE_COLUMNS}
                />
              )}
            </div>
          </>
        )}
      </div>
    </main>
  );
}
